import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  LocalFireDepartment as FireIcon,
  AutoAwesome as StarburstIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// ── Configuración de niveles de prestigio ────────────────────────────────────
// El ranking solo toma en cuenta hasta 60 sesiones (cap competitivo),
// pero la llama visual sigue creciendo para mostrar veteranía.
const PRESTIGE_LEVELS = [
  {
    minDays: 91,
    label: 'Leyenda',
    color: '#FFC95E',          // Púrpura
    glowColor: '#F8B131',
    pulse: true,
    glow: true,
    showCrown: true,
  },
  {
    minDays: 61,
    label: 'Elite',
    color: '#F9BD4E',          // Azul
    glowColor: '#F8B131',
    pulse: true,
    glow: true,
    showCrown: false,
  },
  {
    minDays: 31,
    label: 'Dedicado',
    color: '#F8B131',          // Rojo
    glowColor: '#c8922a',
    pulse: true,
    glow: false,
    showCrown: false,
  },
  {
    minDays: 1,
    label: 'Iniciado',
    color: '#E5A93E',          // Naranja
    glowColor: '#b8842a',
    pulse: false,
    glow: false,
    showCrown: false,
  },
];

function getPrestigeLevel(streakDays) {
  return PRESTIGE_LEVELS.find((level) => streakDays >= level.minDays) ?? null;
}

// ── Componente ───────────────────────────────────────────────────────────────
function StreakFlame({ streakDays, size = 'md', showLabel = true }) {
  if (!streakDays || streakDays < 1) return null;

  const prestige = getPrestigeLevel(streakDays);

  if (!prestige) return null;

  const SIZE_MAP = { sm: 20, md: 28, lg: 36 };
  const FONT_MAP = { sm: '0.7rem', md: '0.8rem', lg: '1rem' };
  const iconSize = SIZE_MAP[size] ?? 28;
  const fontSize = FONT_MAP[size] ?? '0.8rem';

  const tooltipText = streakDays >= 61
    ? `${streakDays} sesiones · ${prestige.label} — Máximo de puntos alcanzado en sesión 60`
    : `${streakDays} sesiones · ${prestige.label}`;

  return (
    <Tooltip title={tooltipText} placement="top" arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          position: 'relative',
          cursor: 'default',
        }}
      >
        {/* Llama */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            ...(prestige.glow && {
              filter: `drop-shadow(0 0 6px ${prestige.glowColor})`,
            }),
            ...(prestige.pulse && {
              animation: 'streakPulse 2s ease-in-out infinite',
              '@keyframes streakPulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.12)' },
              },
            }),
          }}
        >
          <FireIcon sx={{ fontSize: iconSize, color: prestige.color }} />

          {/* Destellos dorados para Leyenda (91+ días) */}
          {prestige.showCrown && (
            <StarburstIcon
              sx={{
                fontSize: iconSize * 0.55,
                color: '#FFC95E',
                position: 'absolute',
                top: -6,
                right: -4,
                filter: 'drop-shadow(0 0 4px #F8B131)',
              }}
            />
          )}
        </Box>

        {/* Contador y etiqueta */}
        {showLabel && (
          <Box sx={{ lineHeight: 1 }}>
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize,
                color: prestige.color,
                lineHeight: 1,
              }}
            >
              {streakDays}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontWeight: 400,
                fontSize: `calc(${fontSize} * 0.85)`,
                color: alpha(prestige.color, 0.7),
                ml: 0.25,
              }}
            >
              ses.
            </Typography>

            {/* Badge de Prestigio cuando supera el cap competitivo */}
            {streakDays > 60 && (
              <Typography
                component="div"
                sx={{
                  fontSize: `calc(${fontSize} * 0.8)`,
                  color: prestige.color,
                  fontWeight: 600,
                  opacity: 0.85,
                  lineHeight: 1,
                  mt: 0.1,
                }}
              >
                {prestige.label}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

StreakFlame.propTypes = {
  streakDays: PropTypes.number.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  showLabel: PropTypes.bool,
};

export default StreakFlame;
